import { useState, useCallback } from 'react';
import { message } from 'antd';
import {
  CFT150,
  FT151,
  CFT152,
  FT153,
  pwt205Api,
  ValidationError,
} from '../../../services/pwt205Api';
import { PWT205Mode } from './usePWT205Mode';

const emptyMaster = (): Partial<CFT150> => ({
  ft150_01: '',
  ft150_11: '',
  ft150_12: '',
  ft150_13: null,
  ft150_14: '',
  ft150_15: '',
  ft150_16: '',
  ft150_17: '',
  ft150_18: 0,
  ft150_19: 0,
  ft150_20: '',
  ft150_21: '',
  ft150_22: '',
  ft150_24: '',
});

export function usePWT205Record(setMode: (mode: PWT205Mode) => void) {
  const [record, setRecord] = useState<CFT150 | null>(null);
  const [formData, setFormData] = useState<Partial<CFT150>>(emptyMaster());
  const [attendees, setAttendees] = useState<Partial<FT151>[]>([]);
  const [topics, setTopics] = useState<Partial<CFT152>[]>([]);
  const [trackings, setTrackings] = useState<Partial<FT153>[]>([]);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const [loading, setLoading] = useState(false);

  const getField = useCallback(
    (key: keyof CFT150) => (formData as Record<string, unknown>)[key as string] ?? '',
    [formData],
  );

  const setField = useCallback((key: keyof CFT150, value: unknown) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  }, []);

  const loadRecord = useCallback(async (id: string) => {
    try {
      setLoading(true);
      const rec = await pwt205Api.get(id);
      setRecord(rec);
      setFormData({ ...rec });
      setAttendees(rec.attendees ?? []);
      setTopics(rec.topics ?? []);
      setTrackings(rec.trackings ?? []);
    } catch {
      message.error('載入失敗');
    } finally {
      setLoading(false);
    }
  }, []);

  const resetForAdd = useCallback(() => {
    setRecord(null);
    const today = new Date().toISOString().substring(0, 10) + 'T00:00:00Z';
    setFormData({ ...emptyMaster(), ft150_13: today });
    setAttendees([]);
    setTopics([]);
    setTrackings([]);
    setValidationErrors([]);
  }, []);

  const revertToRecord = useCallback(() => {
    if (record) {
      setFormData({ ...record });
      setAttendees(record.attendees ?? []);
      setTopics(record.topics ?? []);
      setTrackings(record.trackings ?? []);
    } else {
      resetForAdd();
    }
    setValidationErrors([]);
  }, [record, resetForAdd]);

  const handleSave = useCallback(
    async (isNew: boolean) => {
      setLoading(true);
      setValidationErrors([]);
      try {
        let result: CFT150;
        if (isNew) {
          result = await pwt205Api.create(
            formData,
            attendees,
            topics,
            trackings,
          );
        } else {
          result = await pwt205Api.update(
            formData.ft150_01!,
            formData,
            attendees,
            topics,
            trackings,
          );
        }
        setRecord(result);
        setFormData({ ...result });
        setAttendees(result.attendees ?? []);
        setTopics(result.topics ?? []);
        setTrackings(result.trackings ?? []);
        setMode('browse');
        message.success('儲存成功');
        return result;
      } catch (err: unknown) {
        const e = err as { response?: { status?: number; data?: { errors?: ValidationError[]; error?: string } } };
        if (e.response?.status === 422) {
          const errs = e.response?.data?.errors ?? [];
          setValidationErrors(errs);
          message.error('資料驗證失敗：' + errs.map((x) => x.message).join('、'));
        } else {
          message.error('儲存失敗：' + (e.response?.data?.error ?? '未知錯誤'));
        }
        return null;
      } finally {
        setLoading(false);
      }
    },
    [formData, attendees, topics, trackings, setMode],
  );

  const handleDelete = useCallback(
    async (id: string) => {
      setLoading(true);
      try {
        await pwt205Api.delete(id);
        setRecord(null);
        setFormData(emptyMaster());
        setAttendees([]);
        setTopics([]);
        setTrackings([]);
        setMode('browse');
        message.success('刪除成功');
        return true;
      } catch {
        message.error('刪除失敗');
        return false;
      } finally {
        setLoading(false);
      }
    },
    [setMode],
  );

  // Attendee helpers
  const addAttendeeRow = useCallback(() => {
    setAttendees((prev) => [
      ...prev,
      {
        ft151_02: prev.length + 1,
        ft151_11: '',
        ft151_11a: '',
        ft151_12: '',
        ft151_12a: '',
        ft151_13: formData.ft150_14 ?? '',
        ft151_14: formData.ft150_15 ?? '',
      },
    ]);
  }, [formData.ft150_14, formData.ft150_15]);

  const deleteAttendeeRow = useCallback((idx: number) => {
    setAttendees((prev) =>
      prev
        .filter((_, i) => i !== idx)
        .map((a, i) => ({ ...a, ft151_02: i + 1 })),
    );
  }, []);

  const updateAttendeeRow = useCallback((idx: number, key: keyof FT151, value: unknown) => {
    setAttendees((prev) =>
      prev.map((a, i) => (i === idx ? { ...a, [key]: value } : a)),
    );
  }, []);

  // Topic helpers
  const addTopicRow = useCallback(() => {
    setTopics((prev) => [
      ...prev,
      { ft152_02: prev.length + 1, ft152_11: '', ft152_12: '' },
    ]);
  }, []);

  const deleteTopicRow = useCallback((idx: number) => {
    const deletedTopicSeq = topics[idx]?.ft152_02;
    setTopics((prev) =>
      prev
        .filter((_, i) => i !== idx)
        .map((t, i) => ({ ...t, ft152_02: i + 1 })),
    );
    // Remove trackings for the deleted topic
    if (deletedTopicSeq != null) {
      setTrackings((prev) => prev.filter((t) => t.ft153_02 !== deletedTopicSeq));
    }
  }, [topics]);

  const updateTopicRow = useCallback((idx: number, key: keyof CFT152, value: unknown) => {
    setTopics((prev) =>
      prev.map((t, i) => (i === idx ? { ...t, [key]: value } : t)),
    );
  }, []);

  // Tracking helpers
  const addTrackingRow = useCallback((topicSeq: number) => {
    const existingForTopic = trackings.filter((t) => t.ft153_02 === topicSeq);
    setTrackings((prev) => [
      ...prev,
      {
        ft153_02: topicSeq,
        ft153_03: existingForTopic.length + 1,
        ft153_11: '',
        ft153_12: null,
        ft153_13: '',
        ft153_14: null,
        ft153_15: '',
        ft153_15a: '',
        ft153_16: '',
        ft153_16a: '',
      },
    ]);
  }, [trackings]);

  const deleteTrackingRow = useCallback((topicSeq: number, trkIdx: number) => {
    setTrackings((prev) => {
      const forTopic = prev.filter((t) => t.ft153_02 === topicSeq);
      const others = prev.filter((t) => t.ft153_02 !== topicSeq);
      return [
        ...others,
        ...forTopic
          .filter((_, i) => i !== trkIdx)
          .map((t, i) => ({ ...t, ft153_03: i + 1 })),
      ];
    });
  }, []);

  const updateTrackingRow = useCallback(
    (topicSeq: number, trkIdx: number, key: keyof FT153, value: unknown) => {
      setTrackings((prev) => {
        let count = 0;
        return prev.map((t) => {
          if (t.ft153_02 === topicSeq) {
            if (count === trkIdx) {
              count++;
              return { ...t, [key]: value };
            }
            count++;
          }
          return t;
        });
      });
    },
    [],
  );

  return {
    record,
    formData,
    attendees,
    topics,
    trackings,
    validationErrors,
    loading,
    getField,
    setField,
    loadRecord,
    resetForAdd,
    revertToRecord,
    handleSave,
    handleDelete,
    addAttendeeRow,
    deleteAttendeeRow,
    updateAttendeeRow,
    addTopicRow,
    deleteTopicRow,
    updateTopicRow,
    addTrackingRow,
    deleteTrackingRow,
    updateTrackingRow,
  };
}
